import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded/index'
import { ArrowLeft02Icon, ArrowRight02Icon } from '@hugeicons-pro/core-twotone-rounded/index'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Button, Divider, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import { useSnackbar } from 'notistack'

import { useGetOrderItemsQuery, useUpdateOrderItemsAttributesMutation } from '@/app/services/orderItems'
import {
    useCalculateOrderMutation,
    useGetOrderQuery,
    useSaveOrderProgressMutation,
    useUpdateOrderMutation,
} from '@/app/services/orders'
import { useGetStoreSettingsQuery } from '@/app/services/organisation'
import { selectIsGuestLoggedIn, setOpenGuestLoginDialog } from '@/app/slices/guestUserSlice'
import { setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { formatCurrency, formatNumber } from '@/common/helpers/formatUtilities'
import { getLargestDimensions, ORDER_STATUS_ARRAY, Paths } from '@/common/utils'
import { orderIssuesCount } from '@/common/utils/OrderUtils/OrderUtilities'

const classes = {
    orderSummary: {
        padding: 3,
        gap: 3,
        minHeight: {
            lg: 'calc(100vh - 114px)',
        },
    },
    summaryTitle: {
        fontWeight: 700,
        textTransform: 'uppercase',
    },
    issueMessages: {
        gap: 3,
    },
    pricingSummary: {
        gap: 1.5,
    },
    checkoutButton: {
        gap: 1.5,
        '& a': {
            textDecoration: 'none',
        },
    },
    saveOrder: {
        gap: 1,
    },
}

const OrderReviewSummary = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()
    const { orderId } = useParams()

    const { isAuthenticated } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)

    const { isFetching, isLoading: isLoadingOrder } = useGetOrderQuery({
        organisationId,
        orderId,
    })
    const { data: storeSettings } = useGetStoreSettingsQuery({
        organisationId,
    })

    const { data: order } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const { data: orderItems } = useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const [, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })
    const [, { isLoading: isUpdating }] = useUpdateOrderItemsAttributesMutation({
        fixedCacheKey: 'shared-update-order-items-attributes',
    })

    const [updateOrder] = useUpdateOrderMutation({
        fixedCacheKey: 'shared-update-order',
    })

    const [saveProgress, { isLoading: isSavingProgress }] = useSaveOrderProgressMutation({
        fixedCacheKey: 'shared-save-order-progress',
    })

    const issuesCount = orderIssuesCount(orderItems)

    const largestDimensions = getLargestDimensions(orderItems, storeSettings)

    const prices = useMemo(() => {
        return {
            parts: order?.processingTotalPrice + order?.materialPrice,
            secondaryProcesses: order?.foldingTotalPrice + order?.secondaryProcessTotalPrice,
            miscellaneous: order?.miscellaneousItemsTotalPrice,
            adjustments: order?.otherChargeTotalPrice,
            minimumChargePrice: order?.minimumQuoteChargePrice,
            subtotal: order?.linePrice + (order?.minimumQuoteChargePrice || 0) - (order?.deliveryPrice || 0),
        }
    }, [order])

    const saveOrderProgress = async () => {
        try {
            await saveProgress({
                orderId: order.id,
                organisationId: order.organisationId,
            }).unwrap()
            enqueueSnackbar(t('Order progress details has been sent to your email'), { variant: 'success' })
        } catch (error) {
            console.error(error)
            enqueueSnackbar(t('Error saving order progress'), { variant: 'error' })
        }
    }

    const handleSaveOrderProgress = async () => {
        if (!isGuestLoggedIn) {
            dispatch(setOpenGuestLoginDialog('save-progress'))
        }

        if (isGuestLoggedIn) {
            saveOrderProgress()
        }
    }

    const handleOrderStatusChange = async (order, newOrderStatus) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...order,
                    status: newOrderStatus,
                },
            }).unwrap()
            dispatch(setSelectedOrderStatus(newOrderStatus))
        } catch (_e) {
            const errorMessage = t('Please try again later')
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const navigateToEditOrder = useCallback(async () => {
        handleOrderStatusChange(order, ORDER_STATUS_ARRAY.Calculated)
        navigate(`/orders/${orderId}`)
    }, [handleOrderStatusChange, navigate, orderId, order])

    const navigateToCheckout = useCallback(async () => {
        if (
            (isAuthenticated && order?.quoteUserType === 'Customer') ||
            (isGuestLoggedIn && order?.quoteUserType === 'Guest')
        ) {
            return navigate(Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', orderId))
        }

        dispatch(setOpenGuestLoginDialog('checkout'))
    }, [navigate, orderId, isAuthenticated, order, isGuestLoggedIn])

    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                },
                classes.orderSummary,
            ]}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    sx={classes.summaryTitle}
                    variant="body1"
                >
                    {t('Order summary')}
                </Typography>
            </Box>

            {Boolean(orderItems?.length) && !issuesCount.errors ? (
                <Box
                    sx={[
                        {
                            display: 'flex',
                            flexDirection: 'column',
                        },
                        classes.pricingSummary,
                    ]}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Net weight')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {formatNumber(order.totalMass, storeSettings?.locale)}{' '}
                            {storeSettings?.defaultDrawingUnits === 'Metric' ? 'kg' : 'lb'}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Largest long-dimension')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {largestDimensions.long}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Largest short-dimension')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {largestDimensions.short}
                        </Typography>
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Parts')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {formatCurrency(prices.parts, storeSettings?.currencyCode, storeSettings?.locale)}
                        </Typography>
                    </Box>

                    {prices.secondaryProcesses ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Secondary processes')}</Typography>
                            <Typography
                                className="secondary-processes"
                                variant="strong2"
                            >
                                {formatCurrency(
                                    prices.secondaryProcesses,
                                    storeSettings?.currencyCode,
                                    storeSettings?.locale
                                )}
                            </Typography>
                        </Box>
                    ) : null}

                    {prices.miscellaneous ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Miscellaneous items')}</Typography>
                            <Typography
                                className="miscellaneous"
                                variant="strong2"
                            >
                                {formatCurrency(
                                    prices.miscellaneous,
                                    storeSettings?.currencyCode,
                                    storeSettings?.locale
                                )}
                            </Typography>
                        </Box>
                    ) : null}

                    {prices.adjustments ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Adjustments')}</Typography>
                            <Typography
                                className="adjustments"
                                variant="strong2"
                            >
                                {formatCurrency(prices.adjustments, storeSettings?.currencyCode, storeSettings?.locale)}
                            </Typography>
                        </Box>
                    ) : null}

                    {prices.minimumChargePrice ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Minimum order charge')}</Typography>
                            <Typography
                                className="minimum-charge"
                                variant="strong2"
                            >
                                {formatCurrency(
                                    prices.minimumChargePrice,
                                    storeSettings?.currencyCode,
                                    storeSettings?.locale
                                )}
                            </Typography>
                        </Box>
                    ) : null}
                    <Divider />
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body1">{t('Subtotal')}</Typography>
                        <Typography
                            className="price"
                            variant="strong1"
                        >
                            {formatCurrency(prices.subtotal, storeSettings?.currencyCode, storeSettings?.locale)}
                        </Typography>
                    </Box>
                </Box>
            ) : null}

            <Box
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    classes.checkoutButton,
                ]}
            >
                <Typography variant="small">
                    {t('Proceed to checkout for delivery options and tax calculation')}
                </Typography>

                <Button
                    color="primary"
                    disabled={isLoadingOrder || isFetching || isUpdating || isCalculating}
                    endIcon={<HugeiconsIcon icon={ArrowRight02Icon} />}
                    variant="contained"
                    fullWidth
                    onClick={() => {
                        navigateToCheckout()
                    }}
                >
                    {t('Continue to checkout')}
                </Button>

                <Button
                    color="primary"
                    disabled={isLoadingOrder || isFetching || isUpdating || isCalculating}
                    startIcon={<HugeiconsIcon icon={ArrowLeft02Icon} />}
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                        navigateToEditOrder()
                    }}
                >
                    {t('Edit order')}
                </Button>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    gap: 3,
                }}
            >
                {!isAuthenticated ? (
                    <Box>
                        <Typography
                            mb={1}
                            variant="small"
                        >
                            {t('Not ready to place your order?')}
                        </Typography>
                        <Button
                            color="primary"
                            loading={isSavingProgress}
                            loadingPosition="start"
                            startIcon={<HugeiconsIcon icon={FloppyDiskIcon} />}
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                handleSaveOrderProgress()
                            }}
                        >
                            {t('Save order progress')}
                        </Button>
                    </Box>
                ) : null}
                {storeSettings?.webStoreSupportEmail || storeSettings?.webStoreContactNumber ? (
                    <Box className="needHelp">
                        <Alert
                            severity="info"
                            variant="standard"
                        >
                            <AlertTitle>{t('Need help with your files?')}</AlertTitle>
                            {storeSettings?.webStoreSupportEmail && !storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Contact us at')}{' '}
                                    <a href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </a>
                                </>
                            ) : null}
                            {!storeSettings?.webStoreSupportEmail && storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Call us at')}{' '}
                                    <a href={`tel:${storeSettings?.webStoreContactNumber}`}>
                                        {storeSettings?.webStoreContactNumber}
                                    </a>
                                </>
                            ) : null}
                            {storeSettings?.webStoreSupportEmail && storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Contact us at')}{' '}
                                    <a href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </a>{' '}
                                    {t('or call us at')}{' '}
                                    <a href={`tel:${storeSettings?.webStoreContactNumber}`}>
                                        {storeSettings?.webStoreContactNumber}
                                    </a>
                                </>
                            ) : null}
                        </Alert>
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}

export default OrderReviewSummary
